import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/utils';

export type CardStatus = 'connected' | 'not_configured';

export interface IntegrationCardProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  status: CardStatus;
  isLoading?: boolean;
  children?: ReactNode;
}

interface SlotProps {
  children?: ReactNode;
}

function Meta({ children }: SlotProps): ReactElement {
  return <>{children}</>;
}
Meta.displayName = 'IntegrationCard.Meta';

function Body({ children }: SlotProps): ReactElement {
  return <>{children}</>;
}
Body.displayName = 'IntegrationCard.Body';

function Actions({ children }: SlotProps): ReactElement {
  return <>{children}</>;
}
Actions.displayName = 'IntegrationCard.Actions';

function StatusBadge({ children }: SlotProps): ReactElement {
  return <>{children}</>;
}
StatusBadge.displayName = 'IntegrationCard.StatusBadge';

const PILL_VARIANT_CLASSES: Record<
  CardStatus,
  { pill: string; dot: string; label: string }
> = {
  connected: {
    pill: 'bg-status-success-background border-status-success-border text-status-success-foreground',
    dot: 'bg-status-success-emphasis',
    label: 'Connected',
  },
  not_configured: {
    pill: 'bg-status-warn-background border-status-warn-border text-status-warn-foreground',
    dot: 'bg-status-warn-emphasis',
    label: 'Not Configured',
  },
};

type SlotComponent = (props: SlotProps) => ReactElement;

function findSlot(
  children: ReactNode,
  slotComponent: SlotComponent
): ReactNode {
  let inner: ReactNode = null;
  let found = false;
  Children.forEach(children, child => {
    if (
      !found &&
      isValidElement<SlotProps>(child) &&
      child.type === slotComponent
    ) {
      inner = child.props.children ?? null;
      found = true;
    }
  });
  return inner;
}

export function IntegrationCard(props: IntegrationCardProps): ReactElement {
  const { icon, title, subtitle, status, isLoading, children } = props;
  const metaContent = findSlot(children, Meta);
  const bodyContent = findSlot(children, Body);
  const actionsContent = findSlot(children, Actions);
  const statusBadgeOverride = findSlot(children, StatusBadge);
  const pill = PILL_VARIANT_CLASSES[status];
  const hasStatusBadgeOverride =
    statusBadgeOverride !== null && statusBadgeOverride !== undefined;

  return (
    <div
      data-testid="integration-card"
      className="flex min-h-[170px] flex-col gap-3 rounded-lg border border-card-border bg-card px-6 py-5"
    >
      <div
        data-testid="integration-card-header"
        className="flex items-start justify-between gap-3"
      >
        <div className="flex min-w-0 items-start gap-3">
          <div
            data-testid="integration-card-icon-tile"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-card-border bg-surface-inset text-xl"
          >
            {icon}
          </div>
          <div className="min-w-0">
            <div className="m-0 text-sm font-semibold leading-[21px] text-foreground">
              {title}
            </div>
            <div className="mt-0.5 text-xs leading-[18px] text-muted-foreground">
              {subtitle}
            </div>
          </div>
        </div>

        {!isLoading &&
          (hasStatusBadgeOverride ? (
            <span
              data-testid="integration-card-status-pill-custom"
              className="shrink-0"
            >
              {statusBadgeOverride}
            </span>
          ) : (
            <span
              data-testid="integration-card-status-pill"
              data-status={status}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold',
                pill.pill
              )}
            >
              <span
                data-testid="integration-card-status-pill-dot"
                className={cn('h-1.5 w-1.5 shrink-0 rounded-full', pill.dot)}
              />
              {pill.label}
            </span>
          ))}
      </div>

      {!isLoading && metaContent !== null && metaContent !== undefined && (
        <div
          data-testid="integration-card-meta"
          className="flex flex-wrap gap-4 text-xs text-muted-foreground"
        >
          {metaContent}
        </div>
      )}

      {isLoading ? (
        <div
          data-testid="integration-card-loading-body"
          className="flex flex-col gap-2"
        >
          <div
            data-testid="integration-card-loading-bar"
            className="h-3 w-2/3 animate-pulse rounded bg-muted/60"
          />
          <div
            data-testid="integration-card-loading-bar"
            className="h-3 w-1/2 animate-pulse rounded bg-muted/60"
          />
        </div>
      ) : (
        bodyContent !== null &&
        bodyContent !== undefined && (
          <div data-testid="integration-card-body">{bodyContent}</div>
        )
      )}

      {!isLoading && (
        <div
          data-testid="integration-card-actions"
          className="mt-auto flex gap-2"
        >
          {actionsContent}
        </div>
      )}
    </div>
  );
}
IntegrationCard.displayName = 'IntegrationCard';

IntegrationCard.Meta = Meta;
IntegrationCard.Body = Body;
IntegrationCard.Actions = Actions;
IntegrationCard.StatusBadge = StatusBadge;
